import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PageError } from '@components/layouts/error/page-error.interface';
import { Convenio } from '@interfaces/convenio.interface';
import { SeguroStore } from '@interfaces/seguro-store.interface';
import { ComunService } from '@services/comun.service';
import { Store } from '@services/store.service';
import { from, Observable, of, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class ConveniosService {
  private convenio?: Convenio;

  constructor(
    private http: HttpClient,
    private store: Store<SeguroStore>,
    private comunService: ComunService,
    private router: Router
  ) {}

  private obtenerConvenio(): Convenio {
    const state = this.store.getState();
    this.convenio = state?.convenioResponse;
    return state?.convenioResponse;
  }

  private guardarConvenio(cn: string, enc: boolean = true) {
    const convenioId = +(enc ? this.comunService.desencriptar(cn) : cn);
    const state = this.store.getState();
    this.store.setState({
      ...state,
      convenioResponse: {
        ...state.convenioResponse,
        convenioId,
      },
    });
  }

  obtenerParametrosDelConvenio(
    cn: string,
    enc: boolean = true
  ): Observable<Convenio> {
    cn && this.guardarConvenio(cn, enc);
    const convenio = this.obtenerConvenio();
    cn = convenio?.convenioId.toString();

    if (!cn) {
      this.redireccionarAError();
    }

    this.updateHiddenConvenio();

    return this.http
      .get<Convenio>(`/convenio`, {
        params: {
          cn,
        },
      })
      .pipe(
        switchMap((convenioResponse) => {
          if (convenioResponse === null) {
            throw new Error('Convenio no encontrado');
          }
          const state = this.store.getState();
          this.store.setState({
            ...state,
            convenioResponse,
          });

          if (convenioResponse.enableRecaptchaEnterprise) {
            return from(
              this.cargarRecaptchaScript(
                convenioResponse.reCaptchaEnterprisePublicKey
              )
            ).pipe(
              switchMap(() => {
                return of(convenioResponse);
              })
            );
          } else {
            return of(convenioResponse);
          }
        }),
        catchError((e: HttpErrorResponse) => {
          this.redireccionarAError();
          return throwError(e);
        })
      );
  }

  /**
   * Actualiza el valor del input hidden con el ID "convenio".
   *
   * Este método obtiene el ID del convenio mediante `obtenerConvenio()`, lo encripta usando
   * `comunService.encriptar()`, y lo asigna al input hidden con `id="convenio"`.
   *
   * 📌 **Nota:** El input hidden debe existir en `app.component.html` con la siguiente estructura:
   * ```html
   * <input type="hidden" id="convenio" />
   * ```
   */
  private updateHiddenConvenio() {
    const convenio = this.obtenerConvenio();
    const cn = this.comunService.encriptar(convenio.convenioId.toString());
    const inputConvenio = document.getElementById(
      'convenio'
    ) as HTMLInputElement;

    if (inputConvenio && cn) {
      inputConvenio.value = cn;
    }
  }

  private cargarRecaptchaScript(publicKey: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let script: HTMLScriptElement | null = document.querySelector(
        'script[src*="https://www.google.com/recaptcha/enterprise.js"]'
      );

      if (script) {
        resolve();
      } else {
        script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${publicKey}`;
        script.async = false;

        script.onload = () => {
          resolve();
        };

        script.onerror = (error) => {
          reject(error);
        };

        document.head.appendChild(script);
      }
    });
  }

  public obtenerBannerPorConvenioId(convenioId: number): Observable<string> {
    const sizeOriginalBanner = {
      desktop: {
        width: 1700,
        height: 500,
      },
      tablet: {
        width: 600,
        height: 176,
      },
      mobile: {
        width: 400,
        height: 118,
      },
    };

    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
    const isDesktop = window.innerWidth >= 768;

    const device = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile';
    const { width, height } = sizeOriginalBanner[device];

    return this.http
      .get(`/convenio/ObtenerBanner`, {
        params: {
          convenioId: convenioId.toString(),
          width: width.toString(),
          height: height.toString(),
        },
        responseType: 'blob',
      })
      .pipe(map((blob) => URL.createObjectURL(blob)));
  }

  public obtenerLanding(
    convenioId: string
  ): Observable<{ landingHtml: string }> {
    return this.http.get<{ landingHtml: string }>(`/convenio/ObtenerLanding`, {
      params: {
        cn: convenioId,
      },
    });
  }

  public obtenerMonologo(convenioId: number): Observable<string> {
    return this.http
      .get(`/convenio/ObtenerMonologo`, {
        params: {
          convenioId: convenioId.toString(),
        },
        responseType: 'blob',
      })
      .pipe(map((blob) => URL.createObjectURL(blob)));
  }

  private redireccionarAError() {
    const pageError: PageError = {
      title: 'Error',
      subtitle: 'El convenio no existe',
      description: 'Convenio no encontrado',
      subDescription: 'Inténtelo más tarde',
      txtButton: 'Volver',
      hrefLink: 'https://www.zenitseguros.cl/',
    };

    this.router.navigateByUrl('/error', {
      state: {
        pageError,
      },
    });
  }

  async obtenerHomologacionConvenio(idVirtual: string): Promise<any> {
    const url = `/convenio/ObtenerHomologacionConvenio`;
    let params = new HttpParams().set('idVirtual', idVirtual);
    return this.http.get(url, { params }).toPromise();
  }

  get tieneBanner(): boolean {
    return this.convenio!.tieneBanner;
  }

  get tieneGoogleAnalytics(): boolean {
    return this.convenio?.googleTagManager !== null;
  }

  get tieneReCaptcha(): boolean {
    return this.convenio?.reCaptchaPublicKey !== null;
  }

  get tieneTerminosYCondiciones(): boolean {
    return this.convenio?.terminosYCondiciones?.cuerpo !== null;
  }
}
